import * as angular from 'angular';
import { Growl } from 'angular/custom/services/growl.service';
import Decimal from 'decimal.js';
import { AuthService } from 'app/_services/auth.service';
import { ConfirmModal } from 'app/_services/confirm-modal.factory';
import { LoggedInUser } from 'app/custom';
import { StorageOfferResource } from 'app/storage-offer/storage-offer.resource';

class StorageOfferSellBidsComponentCtrl implements ng.IController {
    // for UI debugging only. Should be set to true before develop merge
    public static readonly SHOW_ONLY_WITH_QUANTITY_AND_PRICE = true;

    private loggedInUser: LoggedInUser;
    public sellBids: StorageOfferSellBidDto[];
    public explodedSellBids: StorageOfferSellBidDto[];
    public offer: StorageOfferDto;

    public loading = true;
    public sortColumn: string = 'timestamp';
    public sortAscending: boolean = false;
    public sellBidToEdit: StorageOfferSellBidDto;
    public sellBidToCreate: StorageOfferSellBidDto;

    constructor(
        private storageOfferResource: StorageOfferResource,
        private authService: AuthService,
        private growl: Growl,
        private confirmModal: ConfirmModal
    ) {}

    $onInit(): void {
        this.authService.userLoginPromise.promise.then((loggedInUser) => {
            this.loggedInUser = loggedInUser;
            this.getSellBids();
            this.initNewSellBid();
        });
    }

    private initNewSellBid() {
        this.sellBidToCreate = {
            offerId: this.offer.id,
            productBids: [
                {
                    quantityValue: null,
                    priority: 1,
                    storageProductId: this.offer.products[0].id,
                    priceAmount: null
                }
            ]
        } as any;
    }

    getSellBids() {
        this.storageOfferResource
            .getSellBids({ id: this.offer.id })
            .$promise.then((sellBids: StorageOfferSellBidDto[]) => {
                sellBids.forEach((sellBid) => {
                    sellBid.productBids.forEach((psb) => {
                        psb.priceAmount.value = new Decimal(
                            psb.priceAmount.value
                        );
                    });
                    sellBid.timestamp = new Date(sellBid.timestamp);
                });
                this.sellBids = sellBids;
                this.loading = false;
                this.explodeSellBids();
            })
            .catch((err) => {
                console.log(err);
                this.growl.show('Could not fetch sell bids', 'danger');
            });
    }

    cancelEditSellBid() {
        this.sellBidToEdit = null;
        this.explodedSellBids.forEach((sb) => {
            delete (sb as any).isEditing;
        });
    }

    saveEditedSellBid(form: ng.IFormController) {
        if (form.$valid) {
            form.$setSubmitted();

            const referenceSellBid = this.sellBids.find(
                (sellBid) => sellBid.id === this.sellBidToEdit.id
            );
            referenceSellBid.shipperEic = this.sellBidToEdit.shipperEic;
            // check if this is part of a bigger sell bid
            if (referenceSellBid.productBids.length === 1) {
                // replace the one changed
                delete (this.sellBidToEdit.productBids[0] as any)
                    .originalStorageProductId;
                referenceSellBid.productBids = [
                    this.sellBidToEdit.productBids[0]
                ];

                this.updateSellBidInternal(referenceSellBid, 'update');
            } else {
                // idea here: as the product might have changed, we first delete all dummy values and the modified one
                // and then fill with dummies again
                const newProductBids = referenceSellBid.productBids.filter(
                    (pb) =>
                        pb.quantityValue !== 0 &&
                        !(pb.priceAmount.value as Decimal).isZero() &&
                        pb.storageProductId !==
                            (this.sellBidToEdit.productBids[0] as any)
                                .originalStorageProductId
                );
                // now re-add the edited one
                delete (this.sellBidToEdit.productBids[0] as any)
                    .originalStorageProductId;
                newProductBids.push(this.sellBidToEdit.productBids[0]);
                referenceSellBid.productBids = newProductBids;
                // and re-fill
                this.fillUpWithEmptyBids(referenceSellBid);
                // and send as update to server
                this.updateSellBidInternal(referenceSellBid, 'update');
            }
        }
    }

    getProductsNotYetBidOnAndOwn(
        sellBid: StorageOfferSellBidDto
    ): StorageProductDto[] {
        const referenceSellBid = this.sellBids.find(
            (sb) => sb.id === sellBid.id
        );
        return this.offer.products.filter((product) => {
            if (
                product.id === sellBid.productBids[0].storageProductId ||
                product.id ===
                    (sellBid.productBids[0] as any).originalStorageProductId
            ) {
                return true;
            }
            // get existing product bid, allow only as choice if not exists or is dummy
            const existingProductBid = referenceSellBid.productBids.find(
                (pb) => pb.storageProductId === product.id
            );
            return (
                !existingProductBid ||
                (existingProductBid.quantityValue === 0 &&
                    (existingProductBid.priceAmount.value as Decimal).isZero())
            );
        });
    }

    editSellBid(sellBid: StorageOfferSellBidDto) {
        this.explodedSellBids.forEach((sb) => ((sb as any).isEditing = false));
        (sellBid as any).isEditing = true;
        this.sellBidToEdit = Object.assign({}, sellBid);
        this.sellBidToEdit.productBids = [];
        this.sellBidToEdit.productBids.push(
            Object.assign({}, sellBid.productBids[0])
        );
        (this.sellBidToEdit.productBids[0] as any).originalStorageProductId =
            this.sellBidToEdit.productBids[0].storageProductId;
    }

    isEditingThisBid(bid: StorageOfferSellBidDto) {
        return (bid as any).isEditing;
    }

    deleteSellBid(sellBidToDelete: StorageOfferSellBidDto) {
        this.confirmModal(
            'Are you sure you want to delete this sell bid?',
            'Confirm deletion'
        ).result.then((res) => {
            if (res) {
                // delete the one product sell bid
                // it can be part of a bigger offer sell bid, in which case we need to set price/quantity to 0 and update it
                const referenceSellBid = this.sellBids.find(
                    (sellBid) => sellBid.id === sellBidToDelete.id
                );
                if (referenceSellBid.productBids.length === 1) {
                    // just delete it
                    this.deleteSellBidInternal(referenceSellBid);
                } else {
                    const referenceProductSellBid =
                        referenceSellBid.productBids.find(
                            (psb) =>
                                psb.storageProductId ===
                                sellBidToDelete.productBids[0].storageProductId
                        );
                    referenceProductSellBid.priceAmount.value = new Decimal(
                        '0'
                    );
                    referenceProductSellBid.quantityValue = 0;
                    referenceProductSellBid.priority = 0;
                    console.log(referenceSellBid);
                    // now check: if every bid now is a 0/0 bid, delete the whole sell bid
                    let isAllEmptyBids = !referenceSellBid.productBids.find(
                        (psb) =>
                            psb.quantityValue !== 0 ||
                            !(psb.priceAmount.value as Decimal).isZero()
                    );
                    if (isAllEmptyBids) {
                        this.deleteSellBidInternal(referenceSellBid);
                    } else {
                        this.updateSellBidInternal(referenceSellBid, 'delete');
                    }
                }
            }
        });
    }

    private updateSellBidInternal(referenceSellBid, method: string) {
        this.storageOfferResource
            .updateSellBid(
                {
                    offerId: referenceSellBid.offerId,
                    id: referenceSellBid.id
                },
                referenceSellBid
            )
            .$promise.then((res) => {
                console.log(res);
                this.getSellBids();
                this.growl.show(`Successfully ${method}d sell bid.`, 'success');
            })
            .catch((err) => {
                console.log(err);
                this.growl.show(`Could not ${method} sell bid.`, 'danger');
            });
    }

    private deleteSellBidInternal(referenceSellBid) {
        this.storageOfferResource
            .deleteSellBid({
                offerId: referenceSellBid.offerId,
                id: referenceSellBid.id
            })
            .$promise.then((res) => {
                this.getSellBids();
                this.growl.show('Successfully deleted sell bid.', 'success');
            })
            .catch((err) => {
                console.log(err);
                this.growl.show('Could not delete sell bid.', 'danger');
            });
    }

    placeSellBid(form: ng.IFormController) {
        if (form.$valid) {
            form.$setSubmitted();
            this.fillUpWithEmptyBids(this.sellBidToCreate);
            this.storageOfferResource
                .placeSellBid({ id: this.offer.id }, this.sellBidToCreate)
                .$promise.then((result) => {
                    console.log(result);
                    this.getSellBids();
                    this.growl.show('Successfully placed sell bid.', 'success');
                    this.initNewSellBid();
                    form.$setPristine();
                    form.$setUntouched();
                })
                .catch((err) => {
                    console.log(err);
                    this.growl.show('Failed to place sell bid.', 'danger');
                });
        }
    }

    private fillUpWithEmptyBids(sellBid: StorageOfferSellBidDto) {
        // fill other products with a 0/0 sell bid
        this.offer.products.forEach((product) => {
            if (
                !sellBid.productBids.find(
                    (bid) => bid.storageProductId === product.id
                )
            ) {
                sellBid.productBids.push({
                    storageProductId: product.id,
                    storageProductName: product.productLabel,
                    priority: 1,
                    quantityValue: 0,
                    priceAmount: {
                        value: '0.0',
                        floatingPointNumber: true
                    }
                });
            }
        });
    }

    public getOfferProductById(id: number): StorageProductDto {
        return this.offer.products.find(
            (product: StorageProductDto) => product.id === id
        );
    }

    /**
     * As the table will show product sell bids and multiple product sell bids can be attached to one offer sell bid,
     * we create a list of "exploded" sell bids, where each offer sell bid only contains exactly one product sell bid
     */
    private explodeSellBids() {
        this.explodedSellBids = [];
        if (this.sellBids) {
            this.sellBids.forEach((sellBid) => {
                sellBid.productBids.forEach((productSellBid) => {
                    if (
                        !(
                            StorageOfferSellBidsComponentCtrl.SHOW_ONLY_WITH_QUANTITY_AND_PRICE &&
                            productSellBid.quantityValue === 0 &&
                            (
                                productSellBid.priceAmount.value as Decimal
                            ).isZero()
                        )
                    ) {
                        const newSellBid = Object.assign({}, sellBid);
                        newSellBid.productBids = [
                            Object.assign({}, productSellBid)
                        ];
                        this.explodedSellBids.push(newSellBid);
                    }
                });
            });
        }
    }

    public changeSortOrder(newSortColumn: string): void {
        if (this.sortColumn === newSortColumn) {
            this.sortAscending = !this.sortAscending;
        } else {
            this.sortColumn = newSortColumn;
            this.sortAscending = true;
        }
    }
}

angular.module('app').component('pkpStorageOfferSellBids', {
    controller: StorageOfferSellBidsComponentCtrl,
    template: require('app/storage-offer-bid/storage-offer-sell-bids.component.html'),
    bindings: {
        offer: '<'
    }
});
